import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router";
import { enqueueSnackbar } from "notistack";
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Typography,
    Paper,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
} from "@mui/material";

import thanq from "../api/thanq";
import useAdminStore from "../store/adminStore";
import UserForm from "../components/UserForm";
import UserInfo from "../components/UserInfo";
import Table from "../components/Table";
import { useReactToPrint } from "react-to-print";
import QRPrint from "../components/QRPrint";

const columns = [
    {
        name: "Profile Pic",
        field: "profileImage",
        cell: (row) => (
            <img
                src={row.profileImage}
                alt="profile"
                style={{ width: 50, height: 50, padding: "5px" }}
            />
        ),
    },
    {
        name: "Handle",
        field: "handleId",
        cell: (row) => row.handleId,
    },
    {
        name: "Phone number",
        field: "phoneNumber",
        cell: (row) => (row.phoneNumber || "-").replace("+91", ""),
    },
    {
        name: "Display Name",
        field: "name",
        cell: (row) => row.name || "N/A",
    },
    {
        name: "Onboarded",
        field: "onboarded",
        cell: (row) => (row.onboarded ? "Yes" : "No"),
        selector: (row) => Boolean(row.onboarded),
        sortable: true,
    },
    {
        name: "ThanQ Points",
        field: "totalQ",
        cell: (row) => row.totalQ || 0,
        selector: (row) => row.totalQ,
        sortable: true,
    },
    {
        name: "Created At",
        field: "createdTime", // ✅ match API key
        cell: (row) => {
            if (!row.createdTime) return "N/A";
            return new Date(row.createdTime).toLocaleString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        selector: (row) => new Date(row.createdTime),
        sortable: true,
    },
];

// Helper function to chunk array into groups of specified size
const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
};

const Users = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isSuperAdmin = useAdminStore((s) => s.isSuperAdmin());
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState({});
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterBy, setFilterBy] = useState("all");
    const [selectedRows, setSelectedRows] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState("");

    const QRListComponent = useRef();

    const handleProfilesPrint = useReactToPrint({
        contentRef: QRListComponent,
        documentTitle: "ThanQ Profile QRs",
        pageStyle: `
            @page {
                size: A4;
                margin: 0 !important;
            }
            @media print {
                * {
                    -webkit-print-color-adjust: exact !important;
                    color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                body {
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .print-page {
                    page-break-after: always;
                    page-break-inside: avoid;
                    height: 100vh;
                    width: 100vw;
                    display: flex !important;
                    flex-direction: row;
                    flex-wrap: wrap;
                    justify-content: space-around;
                    align-items: center;
                    padding: 20px;
                    box-sizing: border-box;
                    margin: 0;
                }
                .print-page:last-child {
                    page-break-after: auto;
                }
                .qr-item {
                    width: 40% !important;
                    height: 40% !important;
                    margin: 20px !important;
                    display: flex !important;
                    justify-content: center !important;
                    align-items: center !important;
                    box-sizing: border-box !important;
                    page-break-inside: avoid !important;
                }
            }
        `,
    });

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await thanq.get(`/admin/users`);
            setUsers(response?.data?.users || []);
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCompanies = useCallback(async () => {
        // Only fetch companies if user is super admin
        if (!isSuperAdmin) {
            return;
        }
        try {
            const { data } = await thanq.get("/admin/companies");
            if (data?.companies) {
                const normalized = data.companies.map((company) => ({
                    id: company.id || company._id || String(Date.now() + Math.random()),
                    name: company.name || "",
                }));
                setCompanies(normalized);
            }
        } catch (err) {
            // Silently fail - companies filter is optional
        }
    }, [isSuperAdmin]);

    // Initial data fetch and URL sync
    useEffect(() => {
        fetchUsers();
        fetchCompanies();
    }, [fetchUsers, fetchCompanies]);

    // Sync selectedCompanyId from URL when searchParams change
    useEffect(() => {
        if (isSuperAdmin) {
            const companyIdFromUrl = searchParams.get("companyId");
            // Only update if the URL value differs from current state
            if (companyIdFromUrl && companyIdFromUrl !== selectedCompanyId) {
                setSelectedCompanyId(companyIdFromUrl);
            } else if (!companyIdFromUrl && selectedCompanyId) {
                // URL was cleared, clear the filter
                setSelectedCompanyId("");
            }
        } else {
            // Clear company filter for non-super admins
            setSelectedCompanyId("");
        }
    }, [isSuperAdmin, searchParams, selectedCompanyId]);

    // Filter users whenever filter criteria change
    useEffect(() => {
        let filtered = [...users];

        if (searchTerm) {
            filtered = filtered.filter(
                (user) =>
                    user.handleId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    user.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by company
        if (selectedCompanyId) {
            filtered = filtered.filter(
                (user) => user.companyId === selectedCompanyId
            );
        }

        if (filterBy !== "all") {
            switch (filterBy) {
                case "onboarded":
                    filtered = filtered.filter((user) => Boolean(user.onboarded));
                    break;
                case "not_onboarded":
                    filtered = filtered.filter((user) => !Boolean(user.onboarded));
                    break;
                case "with_points":
                    filtered = filtered.filter((user) => user.totalQ > 0);
                    break;
                case "no_points":
                    filtered = filtered.filter((user) => user.totalQ === 0);
                    break;
                default:
                    break;
            }
        }
        setFilteredUsers(filtered);
    }, [users, searchTerm, filterBy, selectedCompanyId]);

    const handleAddUser = async (count) => {
        try {
            setLoading(true);
            for (let i = 0; i < count; i++) {
                await thanq.post("/admin/user/");
            }
            await fetchUsers();
            enqueueSnackbar(`Added ${count} new users`, { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error" });
        }
    };

    const handleSaveUser = async (saveData) => {
        try {
            setLoading(true);
            // Update user data
            await thanq.put(`/admin/user/${user.id}`, saveData.formData);

            // Update profile image if changed
            if (saveData.profileImage) {
                await thanq.post(`/admin/user/${user.id}/pic`, { image: saveData.profileImage });
            }

            // Update bank details if they exist
            if (saveData.bankFormData) {
                await thanq.post(`/admin/user/${user.id}/bank`, saveData.bankFormData);
            }

            await fetchUsers();
            enqueueSnackbar("User updated successfully", { variant: "success" });
            setUserModalOpen(false);
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error" });
            throw err; // Re-throw to let the component handle the error
        } finally {
            setLoading(false);
        }
    };

    const handleRowSelected = ({ selectedRows }) => {
        setSelectedRows(selectedRows);
    };

    const handleRowClick = (row) => {
        setUser(row);
        setUserModalOpen(true);
    };

    const handleDeleteUsers = async (userIds) => {
        try {
            const res = await thanq.delete(`/admin/users`, {
                data: { userIds },
            });
            if (res.data.success) {
                setUsers((prev) => prev.filter((u) => !userIds.includes(u.id)));

                enqueueSnackbar(`Deleted ${userIds.length} users`, { variant: "success" });
            } else {
                enqueueSnackbar(res.data.message || "Failed to delete users", { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error" });
        }
    };

    const handleDisableTips = async (userIds) => {
        try {
            const res = await thanq.post(`/admin/users/disable-tips`, { userIds });
            if (res.data.success) {
                // Update local state to reflect the change
                setUsers((prev) =>
                    prev.map((u) =>
                        userIds.includes(u.id) ? { ...u, tipDisabled: true } : u
                    )
                );
                enqueueSnackbar(`Disabled tips for ${userIds.length} users`, { variant: "success" });
            } else {
                enqueueSnackbar(res.data.message || "Failed to disable tips", { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error" });
        }
    };

    const handleEnableTips = async (userIds) => {
        try {
            const res = await thanq.post(`/admin/users/enable-tips`, { userIds });
            if (res.data.success) {
                // Update local state to reflect the change
                setUsers((prev) =>
                    prev.map((u) =>
                        userIds.includes(u.id) ? { ...u, tipDisabled: false } : u
                    )
                );
                enqueueSnackbar(`Enabled tips for ${userIds.length} users`, { variant: "success" });
            } else {
                enqueueSnackbar(res.data.message || "Failed to enable tips", { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error" });
        }
    };

    // Split selected rows into chunks of 4 for printing
    const printPages = chunkArray(selectedRows, 4);

    return (
        <>
            <Table
                loading={loading}
                columns={columns}
                data={filteredUsers}
                title="Users"
                handleRowClick={handleRowClick}
                onSelectedRowsChange={handleRowSelected}
                contextActions={
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            justifyContent: "flex-end",
                            maxWidth: "100%",
                            overflow: "visible",
                        }}
                    >
                        <Button 
                            color="info" 
                            variant="contained" 
                            onClick={handleProfilesPrint}
                            size="small"
                            sx={{ 
                                fontSize: { xs: "0.65rem", sm: "0.8rem" },
                                px: { xs: 0.75, sm: 1.5 },
                                py: { xs: 0.25, sm: 0.5 },
                                minWidth: "auto",
                            }}
                        >
                            Print
                        </Button>
                        <Button
                            color="success"
                            variant="contained"
                            size="small"
                            sx={{ 
                                fontSize: { xs: "0.65rem", sm: "0.8rem" },
                                px: { xs: 0.75, sm: 1.5 },
                                py: { xs: 0.25, sm: 0.5 },
                                minWidth: "auto",
                            }}
                            onClick={() => {
                                let selectedIds = selectedRows.map((row) => row.id);
                                if (selectedIds.length === 0) {
                                    enqueueSnackbar("No users selected", {
                                        variant: "warning",
                                    });
                                    return;
                                }
                                handleEnableTips(selectedIds);
                            }}
                        >
                            Enable
                        </Button>
                        <Button
                            color="warning"
                            variant="contained"
                            size="small"
                            sx={{ 
                                fontSize: { xs: "0.65rem", sm: "0.8rem" },
                                px: { xs: 0.75, sm: 1.5 },
                                py: { xs: 0.25, sm: 0.5 },
                                minWidth: "auto",
                            }}
                            onClick={() => {
                                let selectedIds = selectedRows.map((row) => row.id);
                                if (selectedIds.length === 0) {
                                    enqueueSnackbar("No users selected", {
                                        variant: "warning",
                                    });
                                    return;
                                }
                                handleDisableTips(selectedIds);
                            }}
                        >
                            Disable
                        </Button>
                        <Button
                            color="error"
                            variant="contained"
                            size="small"
                            sx={{ 
                                fontSize: { xs: "0.65rem", sm: "0.8rem" },
                                px: { xs: 0.75, sm: 1.5 },
                                py: { xs: 0.25, sm: 0.5 },
                                minWidth: "auto",
                            }}
                            onClick={() => {
                                let selectedIds = selectedRows.map((row) => row.id);
                                if (selectedIds.length === 0) {
                                    enqueueSnackbar("No users selected", {
                                        variant: "warning",
                                    });
                                    return;
                                }
                                setPendingDeleteIds(selectedIds);
                                setConfirmOpen(true);
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                }
                selectableRows
            />
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <TextField
                        sx={{ width: 400 }}
                        label="Enter handle ID/display name/phone number"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Enter handle ID/display name/phone number"
                    />
                    {isSuperAdmin && (
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Company</InputLabel>
                            <Select
                                value={selectedCompanyId}
                                label="Company"
                                onChange={(e) => {
                                    const newCompanyId = e.target.value;
                                    setSelectedCompanyId(newCompanyId);
                                    // Update URL when filter changes
                                    if (newCompanyId) {
                                        setSearchParams({ companyId: newCompanyId });
                                    } else {
                                        setSearchParams({});
                                    }
                                }}
                            >
                                <MenuItem value="">All Companies</MenuItem>
                                {companies.map((company) => (
                                    <MenuItem key={company.id} value={company.id}>
                                        {company.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Filter by</InputLabel>
                        <Select
                            value={filterBy}
                            label="Filter by"
                            onChange={(e) => setFilterBy(e.target.value)}
                        >
                            <MenuItem value="all">All Users</MenuItem>
                            <MenuItem value="onboarded">Onboarded Users</MenuItem>
                            <MenuItem value="not_onboarded">Not Onboarded</MenuItem>
                            <MenuItem value="with_points">Users with Points</MenuItem>
                            <MenuItem value="no_points">Users without Points</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {filteredUsers.length} of {users.length} users
                        {selectedCompanyId && (
                            <span>
                                {" "}
                                from company: {companies.find((c) => c.id === selectedCompanyId)?.name || selectedCompanyId}
                            </span>
                        )}
                    </Typography>
                </Box>
            </Paper>

            <UserForm onSubmit={handleAddUser} />
            <UserInfo
                user={user}
                onDeleteUser={() => {
                    setPendingDeleteIds([user?.id]);
                    setConfirmOpen(true);
                }}
                open={userModalOpen}
                setOpen={setUserModalOpen}
                onSaveUser={handleSaveUser}
                companies={companies}
                isSuperAdmin={isSuperAdmin}
            />

            <div style={{ display: "none" }}>
                <div ref={QRListComponent}>
                    {printPages.map((page, pageIndex) => (
                        <div key={pageIndex} className="print-page">
                            {page.map((row, idx) => (
                                <div key={row.id || row.handleId || idx} className="qr-item">
                                    <QRPrint
                                        handleId={row.handleId}
                                        phone={row.phoneNumber}
                                        name={row.name}
                                        image={row.profileQR}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{pendingDeleteIds.length}</strong>{" "}
                        {pendingDeleteIds.length === 1 ? "user" : "users"}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            await handleDeleteUsers(pendingDeleteIds);
                            setConfirmOpen(false);
                            setPendingDeleteIds([]);
                        }}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Users;
